/*
 * Asking for a permission (owner, 2026-10-28): location, notifications, the camera. Before the browser's own prompt,
 * a card says what it's for (layout/dialogs: dialog-permission-{kind}); Allow's tap carries through to the browser's
 * prompt, which browsers then show properly (one out of nowhere is often blocked quietly). Nothing shows when it's
 * already allowed; when the browser blocks it, the card says how to turn it back on. `quiet` (asked without a tap:
 * the directory opening, the installed app's first launch, notifications after sign-in) shows nothing for 30 days
 * once Not Now was chosen on this device, or when it's blocked.
 *
 *     if (await ask('camera')) { …getUserMedia… }
 */
const NAMES = { location: 'geolocation', notifications: 'notifications', camera: 'camera' };
const NOT_NOW = 'permission-not-now-';
const WORKED = 'permission-worked-';

/** 'granted', 'denied' or 'prompt' (unknown counts as prompt: Firefox can't say for the camera) */
async function state(kind) {
    if (kind === 'notifications' && 'Notification' in window) {
        return { granted: 'granted', denied: 'denied' }[Notification.permission] ?? 'prompt';
    }

    return (await navigator.permissions?.query({ name: NAMES[kind] }).catch(() => null))?.state ?? 'prompt';
}

/** Not Now chosen on this device in the last 30 days */
const remembered = (kind) => { try { return Date.now() - Number(localStorage.getItem(NOT_NOW + kind) || 0) < 30 * 864e5 } catch { return false } };

/**
 * It worked on this device (a position, a stream, a subscription): the card is skipped from now on, as browsers that
 * can't say a permission is granted (Safari, the iPhone app; Firefox, "Allow While Using") would otherwise show it again
 */
export function worked(kind) {
    try { localStorage.setItem(WORKED + kind, '1') } catch {}
}

const hasWorked = (kind) => { try { return localStorage.getItem(WORKED + kind) === '1' } catch { return false } };

/** Allowed already, so it can be used without any prompt (the browser says granted; Safari's one visit says prompt) */
export async function allowed(kind) {
    return await state(kind) === 'granted';
}

/** Whether to go on to the browser's own request: true when allowed already, or Allow was tapped */
export async function ask(kind, { quiet = false } = {}) {
    const now = await state(kind);

    if (now === 'granted' || (now === 'prompt' && hasWorked(kind))) {
        return true;
    }
    if (quiet && (now === 'denied' || remembered(kind))) {
        return false;
    }

    return new Promise((resolve) => Alpine.store('permission').show(kind, now, resolve));
}

/**
 * This device's position, the card first (as ask()): its coords, or null, with why said unless `quiet`. `precise`
 * asks a phone for GPS rather than its network's guess (a reading from the last 5 minutes will do).
 */
export async function position({ quiet = false, precise = false } = {}) {
    const failed = (text) => { quiet || window.Toaster?.error(text); return null; };

    if (!navigator.geolocation) {
        return failed('This device can\'t give its location. Choose a place instead.');
    }
    if (!await ask('location', { quiet })) {
        return null;
    }

    return new Promise((resolve) => navigator.geolocation.getCurrentPosition(({ coords }) => {
        hasWorked('location') || allowedNow();
        worked('location');
        resolve(coords);
    }, () => resolve(failed('Your location wasn\'t shared. Allow it for this site, or choose a place instead.')),
    { enableHighAccuracy: precise, timeout: precise ? 15000 : 10000, maximumAge: precise ? 300000 : 0 }));
}

/**
 * Location just allowed (owner, 2026-11-02: then GPS it is): `location-allowed` on the window, from the browser's own
 * change (a prompt answered, its settings) or, where it never says (Safari), the first position shared on this device
 */
const allowedNow = () => window.dispatchEvent(new CustomEvent('location-allowed'));
navigator.permissions?.query({ name: 'geolocation' })
    .then((status) => status.addEventListener('change', () => status.state === 'granted' && allowedNow())).catch(() => {});

Alpine.store('permission', {
    kind: null,
    state: 'prompt',
    resolve: null,

    show(kind, state, resolve) {
        this.resolve?.(false);
        Object.assign(this, { kind, state, resolve });
        window.dispatchEvent(new CustomEvent(`dialog-permission-${kind}`, { detail: 'open' }));
    },

    answer(allow) {
        const kind = this.kind;
        try {
            allow ? localStorage.removeItem(NOT_NOW + kind) : localStorage.setItem(NOT_NOW + kind, String(Date.now()));
            // blocked since it worked: the card again until it works again
            this.state === 'denied' && localStorage.removeItem(WORKED + kind);
        } catch {}
        this.finish(allow && this.state !== 'denied');
        window.dispatchEvent(new CustomEvent(`dialog-permission-${kind}`, { detail: 'close' }));
    },

    /** Its card closed (×, Escape, a tap outside): as Not Now, unless answered already */
    closed(kind) {
        kind === this.kind && this.resolve && this.finish(false);
    },

    finish(allowed) {
        const resolve = this.resolve;
        this.resolve = null;
        resolve?.(allowed);
    },
});

// The installed app's first launch: asked once on this device whether distances may use its location (only while
// "GPS (This Device)" chosen there: resources/js/inputs.js, locationSetting)
window.addEventListener('load', async () => {
    const installed = matchMedia('(display-mode: standalone)').matches || navigator.standalone;
    let first = false;
    try {
        first = installed && localStorage.getItem('location-gps') !== 'off' && !localStorage.getItem('permission-launch-location');
        first && localStorage.setItem('permission-launch-location', '1');
    } catch {}

    first && position({ quiet: true });
});
